import {
  CALENDAR_ONLY_TASK_TYPES,
  TASK_TYPES,
  type ColorToken,
  type TaskDTO,
  type TaskType,
} from '@hearth/shared';
import { DEFAULT_LOCALE } from '../../lib/format';

/**
 * Calendar rules as pure functions. Every date here is in the browser's zone and is built with local
 * `Date` constructors/setters, never by adding 86 400 000 ms: a day is 23 or 25 hours across a DST change.
 */

export const CALENDAR_VIEWS = ['day', 'week', 'month', 'agenda'] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];

export type WeekStart = 0 | 1;
export type ColorBy = 'type' | 'person';

/** Pixel height of one hour in the day and week grids. */
export const HOUR_HEIGHT = 48;
/** Drag, resize and click-to-create snap to this many minutes. */
export const SNAP_MINUTES = 15;
/** Length of a new entry, and how tall an entry without an end is drawn. */
export const DEFAULT_BLOCK_MINUTES = 30;
/** Days the agenda shows per page. */
export const AGENDA_DAYS = 30;
export const MINUTES_PER_DAY = 1440;

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

// ------------------------------------------------------------------------------------------------ dates

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** The same wall-clock time `days` later (or earlier). */
export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Midnight on the same day of the month `months` later, clamped to the month's last day (Jan 31 + 1 → Feb 28). */
export function addMonths(date: Date, months: number): Date {
  const lastDay = new Date(date.getFullYear(), date.getMonth() + months + 1, 0).getDate();
  return new Date(date.getFullYear(), date.getMonth() + months, Math.min(date.getDate(), lastDay));
}

export function startOfWeek(date: Date, weekStartsOn: WeekStart): Date {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() - weekStartsOn + 7) % 7));
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). Rounding absorbs 23/25-hour days. */
export function dayDiff(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

/** Minutes since local midnight on the clock face — what the time grid is drawn in. */
export function wallMinutes(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

/** `minutes` past midnight on `day` (1440 is the next midnight). */
export function dateAtMinutes(day: Date, minutes: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes);
}

export function snapMinutes(minutes: number, mode: 'round' | 'floor' | 'ceil' = 'round'): number {
  return Math[mode](minutes / SNAP_MINUTES) * SNAP_MINUTES;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** The next :00 or :30 strictly after `now` — the start the "New" button suggests. */
export function nextHalfHour(now: Date): Date {
  return dateAtMinutes(now, Math.floor(wallMinutes(now) / 30) * 30 + 30);
}

/** `YYYY-MM-DD` from the URL → local midnight; null for anything else, including impossible dates. */
export function parseDateParam(value: string | null): Date | null {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

export function formatDateParam(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseView(value: string | null): CalendarView | null {
  return CALENDAR_VIEWS.find((view) => view === value) ?? null;
}

// ------------------------------------------------------------------------------------------------ ranges

export type RangeOptions = { weekStartsOn: WeekStart; showWeekends: boolean; agendaDays: number };

/** `[start, end)` is what the API is asked for; `days` are the columns (day/week), cells (month) or agenda days. */
export type VisibleRange = { start: Date; end: Date; days: Date[] };

function daysFrom(start: Date, count: number): Date[] {
  return Array.from({ length: count }, (_, i) => addDays(start, i));
}

/**
 * Day: that day. Week: 7 days from the week start; hidden weekends only drop columns — the whole week is still
 * fetched. Month: the 6-week grid around the month (every month fits, and the grid never changes height).
 * Agenda: `agendaDays` days from the date.
 */
export function visibleRange(view: CalendarView, date: Date, options: RangeOptions): VisibleRange {
  const day = startOfDay(date);
  switch (view) {
    case 'day':
      return { start: day, end: addDays(day, 1), days: [day] };
    case 'week': {
      const start = startOfWeek(day, options.weekStartsOn);
      const days = daysFrom(start, 7).filter(
        (d) => options.showWeekends || (d.getDay() !== 0 && d.getDay() !== 6),
      );
      return { start, end: addDays(start, 7), days };
    }
    case 'month': {
      const start = startOfWeek(new Date(day.getFullYear(), day.getMonth(), 1), options.weekStartsOn);
      return { start, end: addDays(start, 42), days: daysFrom(start, 42) };
    }
    case 'agenda':
      return { start: day, end: addDays(day, options.agendaDays), days: daysFrom(day, options.agendaDays) };
  }
}

/** The date the previous/next button goes to. */
export function stepDate(view: CalendarView, date: Date, direction: 1 | -1, agendaDays: number): Date {
  switch (view) {
    case 'day':
      return addDays(startOfDay(date), direction);
    case 'week':
      return addDays(startOfDay(date), 7 * direction);
    case 'month':
      return addMonths(date, direction);
    case 'agenda':
      return addDays(startOfDay(date), agendaDays * direction);
  }
}

/** "Sep 6 – 12, 2026", "Sep 27 – Oct 3, 2026", "Dec 27, 2026 – Jan 2, 2027". */
export function formatDaySpan(first: Date, last: Date, locale = DEFAULT_LOCALE): string {
  const month = new Intl.DateTimeFormat(locale, { month: 'short' });
  if (first.getFullYear() !== last.getFullYear()) {
    const full = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${full.format(first)} – ${full.format(last)}`;
  }
  const end =
    first.getMonth() === last.getMonth() ? `${last.getDate()}` : `${month.format(last)} ${last.getDate()}`;
  return `${month.format(first)} ${first.getDate()} – ${end}, ${last.getFullYear()}`;
}

/** Toolbar title: "September 2026", "Sep 27 – Oct 3, 2026", "Tuesday, September 29, 2026". */
export function periodTitle(
  view: CalendarView,
  date: Date,
  range: VisibleRange,
  locale = DEFAULT_LOCALE,
): string {
  if (view === 'month')
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
  if (view === 'day') {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }
  const first = range.days[0] ?? range.start;
  const last = range.days[range.days.length - 1] ?? addDays(range.end, -1);
  return formatDaySpan(first, last, locale);
}

// ------------------------------------------------------------------------------------------------ entries

export type EntryTiming = {
  start: Date;
  /** Exclusive. An entry without an end is drawn DEFAULT_BLOCK_MINUTES long (never past its own midnight). */
  end: Date;
  /** No `endAt`: a moment rather than a block. */
  point: boolean;
  /** Drawn in the all-day lane and as a bar in the month: all-day entries and blocks of 24 hours or more. */
  allDayLane: boolean;
};

export function entryTiming(task: Pick<TaskDTO, 'dueAt' | 'endAt' | 'allDay'>): EntryTiming | null {
  if (!task.dueAt) return null;
  const start = new Date(task.dueAt);
  if (Number.isNaN(start.getTime())) return null;
  if (task.allDay) {
    const end = task.endAt ? new Date(task.endAt) : addDays(startOfDay(start), 1);
    return { start, end, point: false, allDayLane: true };
  }
  if (!task.endAt) {
    const drawnEnd = Math.min(
      start.getTime() + DEFAULT_BLOCK_MINUTES * MINUTE_MS,
      addDays(startOfDay(start), 1).getTime(),
    );
    return { start, end: new Date(drawnEnd), point: true, allDayLane: false };
  }
  const end = new Date(task.endAt);
  return { start, end, point: false, allDayLane: end.getTime() - start.getTime() >= DAY_MS };
}

/** An entry with its parsed times — what the views render. */
export type CalendarEntry = { task: TaskDTO; timing: EntryTiming };

/** Dated entries, earliest first; longer first on ties so blocks nest predictably. */
export function toCalendarEntries(tasks: readonly TaskDTO[]): CalendarEntry[] {
  const entries: CalendarEntry[] = [];
  for (const task of tasks) {
    const timing = entryTiming(task);
    if (timing) entries.push({ task, timing });
  }
  return entries.sort(
    (a, b) =>
      a.timing.start.getTime() - b.timing.start.getTime() ||
      b.timing.end.getTime() - a.timing.end.getTime() ||
      a.task.title.localeCompare(b.task.title),
  );
}

/** Covers more than one calendar day: shown as a bar (not a chip) in the month. */
export function isMultiDay(timing: EntryTiming): boolean {
  return timing.allDayLane || !isSameDay(timing.start, new Date(timing.end.getTime() - 1));
}

export function coversDay(timing: EntryTiming, day: Date): boolean {
  return timing.start < addDays(day, 1) && timing.end > day;
}

export type DaySegment = {
  dayIndex: number;
  /** Wall-clock minutes within the day, 0–1440. */
  startMinutes: number;
  endMinutes: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
};

/** One piece per visible day an entry touches: a block from 22:00 to 02:00 is drawn on both days. */
export function daySegments(timing: EntryTiming, days: readonly Date[]): DaySegment[] {
  const segments: DaySegment[] = [];
  days.forEach((day, dayIndex) => {
    const next = addDays(day, 1);
    if (timing.start >= next || timing.end <= day) return;
    const startMinutes = timing.start <= day ? 0 : wallMinutes(timing.start);
    const endMinutes = timing.end >= next ? MINUTES_PER_DAY : wallMinutes(timing.end);
    segments.push({
      dayIndex,
      startMinutes,
      // The repeated hour when clocks fall back can put the end "before" the start on the clock face.
      endMinutes: Math.max(endMinutes, startMinutes),
      continuesBefore: timing.start < day,
      continuesAfter: timing.end > next,
    });
  });
  return segments;
}

export type TimedItem = { id: string; startMinutes: number; endMinutes: number };
export type TimedLayout = {
  /** 0-based column within the overlap cluster. */
  column: number;
  /** Columns the cluster is split into; every entry of a transitively overlapping cluster shares it. */
  columns: number;
  /** Columns the entry may widen into (itself included) because nothing to its right overlaps it. */
  span: number;
};

/** Short entries are drawn at least this tall, so they collide by their drawn size. */
export const MIN_DRAWN_MINUTES = 20;

/**
 * Side-by-side layout of one day's timed entries: each entry takes the leftmost column free at its start;
 * entries chained by overlaps form a cluster that splits the day width evenly.
 */
export function layoutTimedEntries(
  items: readonly TimedItem[],
  minMinutes = MIN_DRAWN_MINUTES,
): Map<string, TimedLayout> {
  type Placed = { id: string; start: number; end: number; column: number };
  const sorted = items
    .map((item) => ({
      id: item.id,
      start: item.startMinutes,
      end: Math.max(item.endMinutes, item.startMinutes + minMinutes),
    }))
    .sort((a, b) => a.start - b.start || b.end - a.end || a.id.localeCompare(b.id));
  const result = new Map<string, TimedLayout>();
  let cluster: Placed[] = [];
  let columnEnds: number[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    const columns = columnEnds.length;
    for (const item of cluster) {
      let span = 1;
      for (let column = item.column + 1; column < columns; column += 1) {
        if (
          cluster.some((other) => other.column === column && other.start < item.end && other.end > item.start)
        )
          break;
        span += 1;
      }
      result.set(item.id, { column: item.column, columns, span });
    }
    cluster = [];
    columnEnds = [];
    clusterEnd = -Infinity;
  };

  for (const item of sorted) {
    if (item.start >= clusterEnd) flush();
    let column = columnEnds.findIndex((end) => end <= item.start);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(item.end);
    } else {
      columnEnds[column] = item.end;
    }
    cluster.push({ ...item, column });
    clusterEnd = Math.max(clusterEnd, item.end);
  }
  flush();
  return result;
}

export type WeekItem = { id: string; start: Date; end: Date; bar: boolean };
export type WeekPlacement = {
  id: string;
  startCol: number;
  /** Exclusive. */
  endCol: number;
  lane: number;
  bar: boolean;
  continuesBefore: boolean;
  continuesAfter: boolean;
};

/**
 * Lanes for one week row of the month (or the all-day lane of the week view): bars first — earlier, then longer
 * first — then chips by time, each in the lowest lane free on all the columns it covers. `days` may skip days
 * (hidden weekends); an entry covering only skipped days is left out.
 */
export function layoutMonthWeek(days: readonly Date[], items: readonly WeekItem[]): WeekPlacement[] {
  const spans: { item: WeekItem; startCol: number; endCol: number; first: Date; last: Date }[] = [];
  for (const item of items) {
    let startCol = -1;
    let endCol = -1;
    days.forEach((day, col) => {
      if (item.start >= addDays(day, 1) || item.end <= day) return;
      if (startCol === -1) startCol = col;
      endCol = col + 1;
    });
    const first = days[startCol];
    const last = days[endCol - 1];
    if (first && last) spans.push({ item, startCol, endCol, first, last });
  }
  spans.sort(
    (a, b) =>
      Number(b.item.bar) - Number(a.item.bar) ||
      a.startCol - b.startCol ||
      b.endCol - b.startCol - (a.endCol - a.startCol) ||
      a.item.start.getTime() - b.item.start.getTime(),
  );

  const lanes: boolean[][] = [];
  return spans.map(({ item, startCol, endCol, first, last }) => {
    let lane = 0;
    while (lanes[lane]?.slice(startCol, endCol).some(Boolean)) lane += 1;
    const row = lanes[lane] ?? new Array<boolean>(days.length).fill(false);
    lanes[lane] = row.fill(true, startCol, endCol);
    return {
      id: item.id,
      startCol,
      endCol,
      lane,
      bar: item.bar,
      continuesBefore: item.start < first,
      continuesAfter: item.end > addDays(last, 1),
    };
  });
}

/** Lanes a week row shows when its cells fit `maxLanes`: all of them, or one fewer to make room for "+N more". */
export function visibleLaneCount(placements: readonly WeekPlacement[], maxLanes: number): number {
  const used = placements.reduce((max, p) => Math.max(max, p.lane + 1), 0);
  return used <= maxLanes ? maxLanes : Math.max(0, maxLanes - 1);
}

/** Entries per column that fall below the visible lanes (the "+N more" counts). */
export function hiddenPerColumn(
  placements: readonly WeekPlacement[],
  visibleLanes: number,
  columns: number,
): number[] {
  const hidden = new Array<number>(columns).fill(0);
  for (const p of placements) {
    if (p.lane < visibleLanes) continue;
    for (let col = p.startCol; col < p.endCol; col += 1) hidden[col] = (hidden[col] ?? 0) + 1;
  }
  return hidden;
}

/** Entries that can be completed (notes are informational: never done, never overdue). */
export function isCompletable(type: TaskType): boolean {
  return !CALENDAR_ONLY_TASK_TYPES.includes(type);
}

/** An open entry past its end — or past its start when it has no end. */
export function isOverdue(
  task: Pick<TaskDTO, 'status' | 'type' | 'dueAt' | 'endAt' | 'allDay'>,
  now: Date,
): boolean {
  if (task.status !== 'open' || !isCompletable(task.type)) return false;
  const timing = entryTiming(task);
  if (!timing) return false;
  return (timing.point ? timing.start : timing.end) < now;
}

// ------------------------------------------------------------------------------------------------ rescheduling

/** PATCH fields after a drag. Resizing leaves `dueAt` out so a repeating entry's other starts are untouched. */
export type Reschedule = { dueAt?: string; endAt: string | null };

type Schedulable = Pick<TaskDTO, 'dueAt' | 'endAt' | 'allDay'>;

/** Whole days later/earlier at the same wall-clock time; all-day entries stay on local midnights. */
export function moveByDays(task: Schedulable, days: number): Reschedule | null {
  const timing = entryTiming(task);
  if (!timing) return null;
  const start = addDays(timing.start, days);
  if (task.allDay) return { dueAt: start.toISOString(), endAt: addDays(timing.end, days).toISOString() };
  const endAt = task.endAt
    ? new Date(start.getTime() + (timing.end.getTime() - timing.start.getTime())).toISOString()
    : null;
  return { dueAt: start.toISOString(), endAt };
}

/**
 * A drag in the time grid: the start moves by whole days plus `minutes` on the clock, snapped to 15 minutes and
 * kept within its day; the duration is kept (an entry without an end stays without one).
 */
export function moveTimed(task: Schedulable, days: number, minutes: number): Reschedule | null {
  const timing = entryTiming(task);
  if (!timing || task.allDay) return null;
  const startMinutes = clamp(
    snapMinutes(wallMinutes(timing.start) + minutes),
    0,
    MINUTES_PER_DAY - SNAP_MINUTES,
  );
  const start = dateAtMinutes(addDays(startOfDay(timing.start), days), startMinutes);
  const endAt = task.endAt
    ? new Date(start.getTime() + (timing.end.getTime() - timing.start.getTime())).toISOString()
    : null;
  return { dueAt: start.toISOString(), endAt };
}

/** Dragging the bottom edge: a new end at least 15 minutes after the start (an entry without an end gains one). */
export function resizeTimed(task: Schedulable, end: Date): Reschedule | null {
  const timing = entryTiming(task);
  if (!timing || task.allDay) return null;
  const minEnd = timing.start.getTime() + SNAP_MINUTES * MINUTE_MS;
  return { endAt: new Date(Math.max(end.getTime(), minEnd)).toISOString() };
}

/** False when the drag put the entry back where it was. */
export function changesSchedule(task: Pick<TaskDTO, 'dueAt' | 'endAt'>, change: Reschedule): boolean {
  const same = (a: string | null | undefined, b: string | null) =>
    (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);
  return (change.dueAt !== undefined && !same(change.dueAt, task.dueAt)) || !same(change.endAt, task.endAt);
}

// ------------------------------------------------------------------------------------------------ new entries

export type DraftSlot = { start: Date; end: Date; allDay: boolean };

/** Click in the time grid: a 30-minute block from the 15-minute slot under the pointer. */
export function slotAt(day: Date, minutes: number): DraftSlot {
  const startMinutes = clamp(snapMinutes(minutes, 'floor'), 0, MINUTES_PER_DAY - SNAP_MINUTES);
  return {
    start: dateAtMinutes(day, startMinutes),
    end: dateAtMinutes(day, startMinutes + DEFAULT_BLOCK_MINUTES),
    allDay: false,
  };
}

/** Drag across empty grid on one day: the 15-minute slots between the two pointer positions, both included. */
export function rangeSlot(day: Date, fromMinutes: number, toMinutes: number): DraftSlot {
  const start = clamp(
    snapMinutes(Math.min(fromMinutes, toMinutes), 'floor'),
    0,
    MINUTES_PER_DAY - SNAP_MINUTES,
  );
  const end = clamp(
    snapMinutes(Math.max(fromMinutes, toMinutes), 'ceil'),
    start + SNAP_MINUTES,
    MINUTES_PER_DAY,
  );
  return { start: dateAtMinutes(day, start), end: dateAtMinutes(day, end), allDay: false };
}

/** All-day entry over the days between `a` and `b`, both included (either order). */
export function allDaySlot(a: Date, b: Date): DraftSlot {
  const [first, last] = a <= b ? [a, b] : [b, a];
  return { start: startOfDay(first), end: addDays(startOfDay(last), 1), allDay: true };
}

/** Month click: 9:00 for 30 minutes on that day. */
export function daySlot(day: Date): DraftSlot {
  return slotAt(day, 9 * 60);
}

/**
 * The "All day" switch: a timed slot becomes its start day (a click at 23:45 means that day, not the next one
 * its 30 minutes run into); an all-day slot becomes 9:00 on its first day.
 */
export function toggleSlotAllDay(slot: DraftSlot): DraftSlot {
  return slot.allDay ? daySlot(slot.start) : allDaySlot(slot.start, slot.start);
}

// ------------------------------------------------------------------------------------------------ colors

/** Hues for "color by person", well apart from one another. */
export const PERSON_COLORS: readonly ColorToken[] = [
  'blue',
  'emerald',
  'orange',
  'violet',
  'rose',
  'teal',
  'amber',
  'indigo',
  'lime',
  'pink',
  'sky',
  'purple',
  'green',
  'red',
];

/**
 * A color per user, handed out in id order over the whole team so it never depends on who is on screen and no
 * two people share one until the team outgrows the palette. Unassigned entries are slate.
 */
export function assignPersonColors(userIds: readonly string[]): Map<string, ColorToken> {
  const sorted = [...new Set(userIds)].sort();
  return new Map(sorted.map((id, index) => [id, PERSON_COLORS[index % PERSON_COLORS.length] ?? 'slate']));
}

// ------------------------------------------------------------------------------------------------ text

/** "2:00 p.m." */
export function formatTime(date: Date, locale = DEFAULT_LOCALE): string {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(date);
}

/** "2 p.m." on the hour, else "2:30 p.m." — month chips. */
export function formatCompactTime(date: Date, locale = DEFAULT_LOCALE): string {
  return date.getMinutes() === 0
    ? new Intl.DateTimeFormat(locale, { hour: 'numeric' }).format(date)
    : formatTime(date, locale);
}

/** "2:00 – 3:00 p.m." (one a.m./p.m. when both share it), "10:30 a.m. – 3:00 p.m.". */
export function formatTimeRange(start: Date, end: Date, locale = DEFAULT_LOCALE): string {
  const format = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' });
  const startParts = format.formatToParts(start);
  const period = (parts: Intl.DateTimeFormatPart[]) => parts.find((part) => part.type === 'dayPeriod')?.value;
  const shared = period(startParts) !== undefined && period(startParts) === period(format.formatToParts(end));
  const first = shared
    ? startParts
        .filter((part) => part.type !== 'dayPeriod')
        .map((part) => part.value)
        .join('')
        .trim()
    : format.format(start);
  return `${first} – ${format.format(end)}`;
}

function formatDayLabel(date: Date, now: Date, locale: string): string {
  const withYear = date.getFullYear() !== now.getFullYear();
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: withYear ? 'numeric' : undefined,
  }).format(date);
}

/**
 * When an entry happens, for its details: "Tue, Sep 29 · 2:00 – 3:00 p.m.", "Tue, Sep 29 · 2:00 p.m." (no end),
 * "Tue, Sep 29 – Thu, Oct 1" (all-day; the last day is inclusive), "Tue, Sep 29, 10:00 p.m. – Wed, Sep 30, 2:00 a.m.".
 * The year shows when it is not the current one.
 */
export function describeWhen(
  task: Pick<TaskDTO, 'dueAt' | 'endAt' | 'allDay'>,
  now: Date,
  locale = DEFAULT_LOCALE,
): string {
  const timing = entryTiming(task);
  if (!timing) return '';
  const { start, end } = timing;
  if (task.allDay) {
    const last = new Date(end.getTime() - 1);
    return isSameDay(start, last) || last < start
      ? formatDayLabel(start, now, locale)
      : `${formatDayLabel(start, now, locale)} – ${formatDayLabel(last, now, locale)}`;
  }
  if (timing.point) return `${formatDayLabel(start, now, locale)} · ${formatTime(start, locale)}`;
  if (end <= addDays(startOfDay(start), 1))
    return `${formatDayLabel(start, now, locale)} · ${formatTimeRange(start, end, locale)}`;
  return `${formatDayLabel(start, now, locale)}, ${formatTime(start, locale)} – ${formatDayLabel(end, now, locale)}, ${formatTime(end, locale)}`;
}

/** Accessible name of an entry on the grid: "Call Mario, 2:00 – 3:00 p.m., Pizzeria Uno". */
export function entryLabel(entry: CalendarEntry, now: Date, locale = DEFAULT_LOCALE): string {
  const { task, timing } = entry;
  const when = task.allDay
    ? 'All day'
    : timing.point
      ? formatTime(timing.start, locale)
      : isMultiDay(timing)
        ? describeWhen(task, now, locale)
        : formatTimeRange(timing.start, timing.end, locale);
  return [task.title, when, task.businessName, task.status === 'completed' ? 'done' : null]
    .filter(Boolean)
    .join(', ');
}

// ------------------------------------------------------------------------------------------------ preferences

export type CalendarPreferences = {
  /** User ids and/or 'unassigned' whose entries are shown. */
  people: string[];
  hiddenTypes: TaskType[];
  showCompleted: boolean;
  colorBy: ColorBy;
  weekStartsOn: WeekStart;
  showWeekends: boolean;
};

const PREFERENCES_KEY = 'hearth.calendar';
const PERSON_PATTERN = /^(unassigned|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
/** The API accepts at most this many people per request. */
export const MAX_CALENDAR_PEOPLE = 50;

export function defaultPreferences(userId: string): CalendarPreferences {
  return {
    people: [userId],
    hiddenTypes: [],
    showCompleted: true,
    colorBy: 'type',
    weekStartsOn: 0,
    showWeekends: true,
  };
}

/**
 * Saved choices, field by field (a bad field falls back to its default). The people list belongs to whoever
 * saved it: someone else signing in on this browser starts from their own calendar.
 */
export function loadPreferences(
  userId: string,
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage,
): CalendarPreferences {
  const defaults = defaultPreferences(userId);
  let saved: Record<string, unknown>;
  try {
    const raw = storage?.getItem(PREFERENCES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (typeof parsed !== 'object' || parsed === null) return defaults;
    saved = parsed as Record<string, unknown>;
  } catch {
    return defaults;
  }
  const people =
    saved.userId === userId && Array.isArray(saved.people)
      ? [
          ...new Set(
            saved.people.filter((p): p is string => typeof p === 'string' && PERSON_PATTERN.test(p)),
          ),
        ].slice(0, MAX_CALENDAR_PEOPLE)
      : defaults.people;
  const hiddenTypes = Array.isArray(saved.hiddenTypes)
    ? TASK_TYPES.filter((type) => (saved.hiddenTypes as unknown[]).includes(type))
    : [];
  return {
    people,
    hiddenTypes,
    showCompleted: typeof saved.showCompleted === 'boolean' ? saved.showCompleted : defaults.showCompleted,
    colorBy: saved.colorBy === 'person' ? 'person' : 'type',
    weekStartsOn: saved.weekStartsOn === 1 ? 1 : 0,
    showWeekends: typeof saved.showWeekends === 'boolean' ? saved.showWeekends : defaults.showWeekends,
  };
}

export function savePreferences(
  userId: string,
  preferences: CalendarPreferences,
  storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage,
): void {
  try {
    storage?.setItem(PREFERENCES_KEY, JSON.stringify({ userId, ...preferences }));
  } catch {
    // A full or unavailable localStorage only means the choices are not remembered.
  }
}

/** GET /api/calendar parameters, or null when nobody or no type is selected (the API would default to "me"). */
export function calendarQueryParams(range: VisibleRange, preferences: CalendarPreferences) {
  const types = TASK_TYPES.filter((type) => !preferences.hiddenTypes.includes(type));
  if (preferences.people.length === 0 || types.length === 0) return null;
  return {
    from: range.start.toISOString(),
    to: range.end.toISOString(),
    users: [...preferences.people].sort(),
    ...(types.length < TASK_TYPES.length ? { types } : {}),
    includeCompleted: preferences.showCompleted,
  };
}
